import { z } from "zod";

import { assertNever } from "./errors.js";
import { getNormalEnum } from "./pseudoEnums.js";

export type EnumValues<T> = T[keyof T];

export const FolderRootType = {
  USER_DRAFTS_ROOT: "USER_DRAFTS_ROOT",
  SPACE_ROOT: "SPACE_ROOT",
} as const;
export type FolderRootType = EnumValues<typeof FolderRootType>;

export const FolderEntryType = {
  ...FolderRootType,
  FOLDER: "FOLDER",
  LEAF: "LEAF",
} as const;
export type FolderEntryType = EnumValues<typeof FolderEntryType>;

export const FolderEntryLeafType = {
  PROJECT: "PROJECT",
} as const;
export type FolderEntryLeafType = EnumValues<typeof FolderEntryLeafType>;

export const FOLDER_ENTRY_CONTAINER_TYPES = [
  FolderEntryType.SPACE_ROOT,
  FolderEntryType.USER_DRAFTS_ROOT,
  FolderEntryType.FOLDER,
] as const;

export const FolderEntryPermissionLevel = {
  /** Compatibility level for app-only grants; not exposed in folder sharing UI. */
  VIEW_APP: "VIEW_APP",
  VIEW: "VIEW",
  EDIT: "EDIT",
  FULL_ACCESS: "FULL_ACCESS",
} as const;
export type FolderEntryPermissionLevel = EnumValues<
  typeof FolderEntryPermissionLevel
>;

export const OutputType = {
  STREAM: "STREAM",
  ERROR: "ERROR",
  EXECUTE_RESULT: "EXECUTE_RESULT",
  METADATA: "METADATA",
} as const;
export type OutputType = EnumValues<typeof OutputType>;

export const AppRunState = {
  RUNNING: "RUNNING",
  IDLE: "IDLE",
  ERRORED: "ERRORED",
} as const;
export type AppRunState = EnumValues<typeof AppRunState>;

export const KernelStateEnumLiterals = z.enum([
  "STARTING",
  "RUNNING",
  "STOPPING",
  "STOPPED",
]);
export const KernelState = getNormalEnum(KernelStateEnumLiterals);
export type KernelState = z.infer<typeof KernelStateEnumLiterals>;

export const KernelStoppedReasonLiteral = z.enum([
  /** This is a generic stopped reason for when no additional detail is known */
  "STOPPED",
  "OUT_OF_KERNELS",
  "OVER_USER_KERNEL_LIMIT",
  "PAID_COMPUTE_DISABLED",
  "USER_KILLED",
  "OOM_KILLED",
  "TIMED_OUT",
  /** Hit the hard wall-clock lifetime cap, regardless of activity */
  "LIFETIME_EXCEEDED",
  "ERRORED",
]);
export const KernelStoppedReason = getNormalEnum(KernelStoppedReasonLiteral);
export type KernelStoppedReason = z.infer<typeof KernelStoppedReasonLiteral>;

export const isKernelStoppedReasonFailed = (
  stoppedReason: KernelStoppedReason,
): boolean => {
  return {
    [KernelStoppedReason.ERRORED]: true,
    [KernelStoppedReason.OUT_OF_KERNELS]: true,
    [KernelStoppedReason.OVER_USER_KERNEL_LIMIT]: true,
    [KernelStoppedReason.PAID_COMPUTE_DISABLED]: true,
    [KernelStoppedReason.OOM_KILLED]: true,

    // These are special cased depending on AppRunState
    [KernelStoppedReason.TIMED_OUT]: false,
    [KernelStoppedReason.LIFETIME_EXCEEDED]: false,
    [KernelStoppedReason.USER_KILLED]: false,

    [KernelStoppedReason.STOPPED]: false,
  }[stoppedReason];
};

export const FAILED_KERNEL_STOPPED_REASONS = Object.values(
  KernelStoppedReason,
).filter(isKernelStoppedReasonFailed);

export const isKernelStoppedReasonFailedIfPending = (
  stoppedReason: KernelStoppedReason,
): boolean => {
  return {
    [KernelStoppedReason.ERRORED]: false,
    [KernelStoppedReason.OUT_OF_KERNELS]: false,
    [KernelStoppedReason.OVER_USER_KERNEL_LIMIT]: false,
    [KernelStoppedReason.PAID_COMPUTE_DISABLED]: false,
    [KernelStoppedReason.OOM_KILLED]: false,

    [KernelStoppedReason.TIMED_OUT]: true,
    [KernelStoppedReason.LIFETIME_EXCEEDED]: true,
    [KernelStoppedReason.USER_KILLED]: true,

    [KernelStoppedReason.STOPPED]: false,
  }[stoppedReason];
};

export const FAILED_KERNEL_STOPPED_REASONS_IF_PENDING = Object.values(
  KernelStoppedReason,
).filter(isKernelStoppedReasonFailedIfPending);

export const isAppSessionErrored = ({
  kernelState,
  kernelStoppedReason,
  state,
}: {
  state: AppRunState;
  kernelState: KernelState;
  kernelStoppedReason: KernelStoppedReason | null;
}): boolean => {
  if (state === AppRunState.ERRORED) {
    return true;
  } else if (
    kernelState === KernelState.STOPPED &&
    kernelStoppedReason != null
  ) {
    if (isKernelStoppedReasonFailed(kernelStoppedReason)) {
      return true;
    } else if (
      state === AppRunState.RUNNING &&
      isKernelStoppedReasonFailedIfPending(kernelStoppedReason)
    ) {
      return true;
    }
  }

  return false;
};

export const isAppSessionRunning = ({
  kernelState,
  kernelStoppedReason,
  state,
}: {
  state: AppRunState;
  kernelState: KernelState;
  kernelStoppedReason: KernelStoppedReason | null;
}): boolean => {
  if (
    isAppSessionErrored({
      kernelState,
      kernelStoppedReason,
      state,
    })
  ) {
    return false;
  } else if (state === AppRunState.RUNNING) {
    return true;
  } else {
    return false;
  }
};

export const isAppSessionIdle = ({
  kernelState,
  kernelStoppedReason,
  state,
}: {
  state: AppRunState;
  kernelState: KernelState;
  kernelStoppedReason: KernelStoppedReason | null;
}): boolean => {
  if (
    isAppSessionErrored({
      kernelState,
      kernelStoppedReason,
      state,
    })
  ) {
    return false;
  } else if (
    isAppSessionRunning({
      kernelState,
      kernelStoppedReason,
      state,
    })
  ) {
    return false;
  } else {
    return true;
  }
};

/**
 * Special version of isAppSessionRunning that also checks
 * to see if the app session has run at least once
 */
export const isAppSessionPending = ({
  executionRevision,
  kernelState,
  kernelStoppedReason,
  state,
  useEvents,
}: {
  state: AppRunState;
  kernelState: KernelState;
  kernelStoppedReason: KernelStoppedReason | null;
  executionRevision: number;
  useEvents: boolean;
}): boolean => {
  return (
    isAppSessionRunning({ state, kernelStoppedReason, kernelState }) ||
    (useEvents && executionRevision === 0)
  );
};

export const KernelFileDownloadState = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  FAILED: "FAILED",
} as const;
export type KernelFileDownloadState = EnumValues<
  typeof KernelFileDownloadState
>;

export const DataSourceFilter = {
  ENDORSED_DATA: "ENDORSED_DATA",
  ALL_DATA: "ALL_DATA",
  /** @deprecated - We no longer support only filtering to semantic projects */
  SEMANTIC_PROJECTS: "SEMANTIC_PROJECTS",
} as const;
export type DataSourceFilter = EnumValues<typeof DataSourceFilter>;
/** Zod schema for {@link DataSourceFilter}, derived from the enum const. */
export const zDataSourceFilter = z.nativeEnum(DataSourceFilter);

export const convertDeprecatedDataSourceFilter = (
  dataSourceFilter: DataSourceFilter | "DATA_WAREHOUSE_TABLES",
): DataSourceFilter => {
  if (dataSourceFilter === "DATA_WAREHOUSE_TABLES") {
    return "ALL_DATA";
  }
  if (dataSourceFilter === "SEMANTIC_PROJECTS") {
    return "ENDORSED_DATA";
  }
  return dataSourceFilter;
};

export const FileUploadState = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  DONE: "DONE",
  CANCELLED: "CANCELLED",
  ERRORED: "ERRORED",
} as const;
export type FileUploadState = EnumValues<typeof FileUploadState>;

export const CsvDownloadModeLiteral = z.enum([
  "PREVIEW", // Preview dataframe for a query mode query
  "SOURCE", // The source dataframe for a cell
  "DEFAULT", // The output dataframe for a cell
]);
export type CsvDownloadMode = z.infer<typeof CsvDownloadModeLiteral>;
export const CsvDownloadMode = getNormalEnum(CsvDownloadModeLiteral);

export const ExportActionLiteral = z.enum([
  "COPY_TO_CLIPBOARD",
  "DOWNLOAD_AS_CSV",
]);
export type ExportAction = z.infer<typeof ExportActionLiteral>;
export const ExportAction = getNormalEnum(ExportActionLiteral);

export const CellExecutionState = {
  RUNNING: "RUNNING",
  IDLE: "IDLE",
  QUEUED: "QUEUED",
  ERRORED: "ERRORED",
  CANCELLED: "CANCELLED",
  STALE: "STALE",
} as const;
export type CellExecutionState = EnumValues<typeof CellExecutionState>;

/**
 * It's possible for an app session cell to have the state of RUNNING
 * but the kernel/session to not be running. This utility helps check for that case
 */
export function resolvedCellExecutionState(
  appSession: {
    state: AppRunState;
    kernelState: KernelState;
    kernelStoppedReason: KernelStoppedReason | null;
  },
  { state: cellState }: { state: CellExecutionState },
): CellExecutionState {
  if (
    cellState === CellExecutionState.RUNNING ||
    cellState === CellExecutionState.QUEUED
  ) {
    if (isAppSessionErrored(appSession)) {
      return CellExecutionState.CANCELLED;
    } else {
      return cellState;
    }
  } else {
    return cellState;
  }
}

export const CellType = {
  CODE: "CODE",
  MARKDOWN: "MARKDOWN",
  DISPLAY_TABLE: "DISPLAY_TABLE",
  INPUT: "INPUT",
  SQL: "SQL",
  VEGA_CHART: "VEGA_CHART",
  CELL_GROUP: "CELL_GROUP",
  METRIC: "METRIC",
  TEXT: "TEXT",
  MAP: "MAP",
  WRITEBACK: "WRITEBACK",
  DBT_METRIC: "DBT_METRIC",
  PIVOT: "PIVOT",
  FILTER: "FILTER",
  COMPONENT_IMPORT: "COMPONENT_IMPORT",
  CHART: "CHART",
  BLOCK: "BLOCK",
  EXPLORE: "EXPLORE",
  COLLAPSIBLE: "COLLAPSIBLE",
} as const;
export type CellType = EnumValues<typeof CellType>;

export const CellTypeToDisplayName: Record<CellType, string> = {
  [CellType.INPUT]: "Input",
  [CellType.MARKDOWN]: "Markdown",
  [CellType.CODE]: "Code",
  [CellType.SQL]: "SQL",
  [CellType.VEGA_CHART]: "Deprecated chart",
  [CellType.DISPLAY_TABLE]: "Table",
  [CellType.METRIC]: "Single value",
  [CellType.CELL_GROUP]: "Cell Group",
  [CellType.TEXT]: "Text",
  [CellType.MAP]: "Map",
  [CellType.WRITEBACK]: "Writeback",
  [CellType.DBT_METRIC]: "dbt Semantic Layer",
  [CellType.PIVOT]: "Legacy pivot",
  [CellType.FILTER]: "Filter",
  [CellType.COMPONENT_IMPORT]: "Component",
  [CellType.CHART]: "Legacy chart",
  [CellType.BLOCK]: "Block",
  [CellType.EXPLORE]: "Explore",
  [CellType.COLLAPSIBLE]: "Section",
};

export const CommentableEntity = {
  CELL: "CELL",
  COMMENT: "COMMENT",
} as const;
export type CommentableEntity = EnumValues<typeof CommentableEntity>;

export const SpecialVersionType = {
  LAST_PUBLISHED: "latest",
  DRAFT: "draft",
} as const;
export type SpecialVersionType = EnumValues<typeof SpecialVersionType>;

/**
 * An intrinsic group is one that does not actually exist in the database,
 * rather membership is determined by user attributes
 * at runtime
 */
export const IntrinsicGroupId = {
  /**
   * Includes users in a workspace at org role ADMIN
   */
  ADMINS: "ADMINS",
  /**
   * Includes users in a workspace at org role MEMBER and above
   */
  MEMBERS: "MEMBERS",
  /**
   * Includes users in a workspace only at org role GUEST
   */
  GUESTS: "GUESTS",
  /**
   * Includes users in a workspace at org role ANONYMOUS and above
   */
  PUBLIC: "PUBLIC",
} as const;
export type IntrinsicGroupId = EnumValues<typeof IntrinsicGroupId>;

/**
 * Roles for users across an entire deployment of hex.
 */
export const PlatformRole = {
  /**
   * @deprecated Super admin that can create/edit all orgs.
   * Please avoid using PlatformRole.ADMIN. While not officially deprecated, we are working to minimize platform admin's importance in favor of a new
   * auth mechanism that will allow plat admins additional capabilities. Please check in with the auth team before using this role.
   *
   */
  ADMIN: "ADMIN",
  /**
   * Admin with the same read permissions as PlatformRole.ADMIN, but no write permissions.
   */
  ADMIN_READ_ONLY: "ADMIN_READ_ONLY",
  /**
   * Everyone else.
   */
  MEMBER: "MEMBER",
} as const;
export type PlatformRole = EnumValues<typeof PlatformRole>;

/**
 * Roles for users in an org, in descending order of permission scope.
 *
 * Each role's capabilities are generally a superset of the ones beneath it.
 */
export const OrgRoleLiteral = z.enum([
  /**
   * Can edit org membership and workspace assets.
   */
  "ADMIN",
  /**
   * Can create and edit projects.
   * Has some limited org-level permissions.
   */
  "MANAGER",
  /**
   * Able to create and edit projects.
   *
   * Also referred to as Author.
   */
  "EDITOR",
  /**
   * Able to create "Explores"
   */
  "EXPLORER",
  /**
   * Able to view projects if given Role.USER or Role.VIEWER perms
   */
  "MEMBER",
  /**
   * Someone who is not part of the org, only has access to projects/assets
   * explicility shared with them.
   */
  "GUEST",
  /**
   * Role granted to users via signed embedding API
   */
  "EMBEDDED_USER",
  /**
   * Ephemeral user for viewing publicly shared Hexes
   */
  "ANONYMOUS",
]);

export type OrgRole = z.infer<typeof OrgRoleLiteral>;
export const OrgRole = getNormalEnum(OrgRoleLiteral);

/**
 * Permissible roles for Ai Agents.
 */
export type AiAgentOrgRoles =
  | Extract<OrgRole, "ADMIN" | "MANAGER" | "EDITOR" | "EXPLORER">
  | "HEX_SUPPORT_EMAIL";

/**
 * Roles for users in a group, in descending order of permission scope.
 *
 * Each role's capabilities are generally a superset of the ones beneath it.
 */
export const GroupRole = {
  /**
   * Can create/view projects in group
   */
  MEMBER: "MEMBER",
} as const;
export type GroupRole = EnumValues<typeof GroupRole>;

/**
 * Roles that a user or group can have on a project
 */
export const ProjectRoleLiteral = z.enum([
  /**
   * Can edit, change permissions, and delete
   */
  "OWNER",
  /**
   * Can edit logic. Can also change permissions if allowed by OWNER
   */
  "EDITOR",
  /**
   * Can view logic
   */
  "VIEWER",
  /**
   * Can only run the published app
   */
  "APP_USER",
]);
export type ProjectRole = z.infer<typeof ProjectRoleLiteral>;
export const ProjectRole = getNormalEnum(ProjectRoleLiteral);

// TODO: Add APP_USER once Tao apps have an app-view capability.
const TaoProjectRoleLiteral = z.enum(["VIEWER", "EDITOR", "OWNER"]);
export type TaoProjectRole = z.infer<typeof TaoProjectRoleLiteral>;
export const TaoProjectRole = getNormalEnum(TaoProjectRoleLiteral);

/**
 * Roles that a user or group can have on an app session
 *
 * @deprecated The app session role for every grant will always be an owner.
 * If the user is the creator of the app session, assume that they are the OWNER of the app session.
 */
export const AppSessionRole = {
  /**
   * Can run/edit and change share setting of the app session
   *
   * This is granted to the creator of an app session.
   */
  OWNER: "OWNER",
} as const;
export type AppSessionRole = EnumValues<typeof AppSessionRole>;

/**
 * Roles that a user or group can have on a shared asset
 */
export const SharedAssetRole = {
  /**
   * Can import the asset into a project and write code using the asset
   */
  IMPORTER: "IMPORTER",

  /**
   * Can view results derived from the shared asset (e.g. query results in app view).
   *
   * Semantically closer to `DERIVED_RESULTS_VIEWER`.
   */
  VIEWER: "VIEWER",
} as const;
export type SharedAssetRole = EnumValues<typeof SharedAssetRole>;

/**
 * How an agent chat thread interacted with a linked Hex entity.
 */
export const AgentChatThreadLinkInteraction = {
  /**
   * The thread read the entity as context without changing it.
   */
  READ: "READ",

  /**
   * The thread executed against and may have changed the entity.
   */
  EDITED: "EDITED",

  /**
   * The thread created the entity, e.g. the unlisted project backing a
   * homepage thread.
   */
  CREATED: "CREATED",
} as const;
export type AgentChatThreadLinkInteraction = EnumValues<
  typeof AgentChatThreadLinkInteraction
>;

/**
 * Roles that a user or group can have on a Collection
 */
export const CollectionRole = {
  /**
   * Can edit the collection, modify collection membership, and add/remove projects from the collection
   * Has ProjectRole defined on the hex_collection_link for each project within the collection
   */
  MANAGER: "MANAGER",

  /**
   * Has ProjectRole defined on the hex_collection_link for each project within the collection
   */
  MEMBER: "MEMBER",
} as const;
export type CollectionRole = EnumValues<typeof CollectionRole>;

/**
 * Roles on entities, _always_ check the underlying entity for what each role means in context
 */
export const Role = {
  /**
   * See underlying entity docs for role meaning in context
   */
  OWNER: "OWNER",
  /**
   * See underlying entity docs for role meaning in context
   */
  EDITOR: "EDITOR",
  /**
   * See underlying entity docs for role meaning in context
   */
  VIEWER: "VIEWER",
  /**
   * See underlying entity docs for role meaning in context
   */
  USER: "USER",
} as const;
export type Role = EnumValues<typeof Role>;

/**
 * Sources of org role requests (used for analytics tracking).
 */
export const OrgRoleRequestSourceLiteral = z.enum([
  "APP_DRILLDOWN", // drilldown menu in published app element
  /** @deprecated - legacy naming before ask-from-app merge */
  "APP_AGENT", // app-agent sidebar in a published app
  "APP_EXPLORE", // explore-from-here button on published app element
  "CREATE_EXPLORE", // "New explore" button or no-permissions state in Explorations page
  "OPEN_EXPLORE", // no-permissions state in an explore opened from a link
  "CONVERT_EXPLORE_TO_PROJECT", // Explore UI CTA to convert to project
  "CREATE_PROJECT", // "Create new" button in project page
  "CREATE_COMPONENT", // "Create new" button in component page
  "CREATE_COLLECTION", // "Create new" button in collection page
  "LOGIC_BANNER", // Logic banner in project page
  "DATA_HOME_TAB",
  "EXPORT_GOOGLE_SHEET", // Google sheet export in footer
  "TASKS", // gated task actions on the Tasks page and task detail view
]);

export type OrgRoleRequestSource = z.infer<typeof OrgRoleRequestSourceLiteral>;
export const OrgRoleRequestSource = getNormalEnum(OrgRoleRequestSourceLiteral);

/**
 * Actions that can be gated with a prompt to request an org role (used for analytics tracking)
 */
export const OrgRoleRequestGatedActionLiteral = z.enum([
  "EXPLORE",
  "CREATE_PROJECT",
  "CREATE_COMPONENT",
  "CREATE_COLLECTION",
  "EDIT_PROJECT",
  "EDIT_COMPONENT",
  "BROWSE_DATA",
  "CREATE_THREAD",
  "EXPORT_GOOGLE_SHEET",
  "CREATE_TASK",
  "RUN_TASK",
  "EDIT_TASK",
  "MANAGE_TASK",
]);
export type OrgRoleRequestGatedAction = z.infer<
  typeof OrgRoleRequestGatedActionLiteral
>;
export const OrgRoleRequestGatedAction = getNormalEnum(
  OrgRoleRequestGatedActionLiteral,
);

export const LayoutType = {
  CANVAS: "CANVAS",
  DATA_APP: "DATA_APP",
  GRID: "GRID",
  STORY: "STORY",
} as const;
export type LayoutType = EnumValues<typeof LayoutType>;

export const CellRunModeLiterals = z.enum([
  "CELL_ONLY",
  "CELL_AND_ABOVE",
  "CELL_AND_BELOW",
  "ALL_CELLS",
]);

export const CellRunMode = getNormalEnum(CellRunModeLiterals);
export type CellRunMode = z.infer<typeof CellRunModeLiterals>;

export const ProjectLanguageLiteral = z.enum(["PYTHON", "R"]);
export type ProjectLanguage = z.infer<typeof ProjectLanguageLiteral>;
export const ProjectLanguage = getNormalEnum(ProjectLanguageLiteral);

export const ProjectEmbeddingStatusLiteral = z.enum([
  "NOT_PROCESSED", // Default status. It means that the project has never been checked for embedding status.
  "PROCESSING", // Status when the project is being checked for embedding status.
  "EMBEDDED", // Status of the Hex when it is embedded or represented in LanceTableOfHexes.
  "NOT_EMBEDDED", // Status of the Hex when it is not embedded or represented in LanceTableOfHexes.
  "TO_BE_EMBEDDED", // Status of the Hex when it is considered "high quality" and will be picked up to be embedded in Lance.
  "TO_BE_DELETED", // Status of the Hex when it is considered "low quality" or is deleted from the workspace. This will eventually be deleted from the Lance table during garbage collection process.
  "PROCESSING_DELETE", // Status of the Hex when it is being processed by Lance GC queue and deleted from the Lance table.
]);
export type ProjectEmbeddingStatus = z.infer<
  typeof ProjectEmbeddingStatusLiteral
>;
export const ProjectEmbeddingStatus = getNormalEnum(
  ProjectEmbeddingStatusLiteral,
);

// This is a hack. We will want to figure out what we want to do long term
export const ProjectLanguageLiteralLowercase = z.enum(["python", "r"]);
export type ProjectLanguageLowercase = z.infer<
  typeof ProjectLanguageLiteralLowercase
>;
export const ProjectLanguageLowercase = getNormalEnum(
  ProjectLanguageLiteralLowercase,
);

export const CellOutputType = {
  PANDAS: "PANDAS",
  SNOWPARK: "SNOWPARK",
  BIGFRAMES: "BIGFRAMES",
} as const;
export type CellOutputType = EnumValues<typeof CellOutputType>;

export const SchemaElementSearchResultType = {
  DATABASES: "DATABASES",
  SCHEMAS: "SCHEMAS",
  TABLES: "TABLES",
  COLUMNS: "COLUMNS",
} as const;
export type SchemaElementSearchResultType = EnumValues<
  typeof SchemaElementSearchResultType
>;

export const SchemaElementSearchAlgorithm = {
  SUBSTRING: "SUBSTRING",
  PREFIX: "PREFIX",
  EXACT: "EXACT",
} as const;
export type SchemaElementSearchAlgorithm = EnumValues<
  typeof SchemaElementSearchAlgorithm
>;

export const HexOwnershipLevel = {
  /**
   * Projects created by the querying user (TODO: should this be called creator or self instead?)
   */
  OWN: "OWN",
  /**
   * Projects shared with the querying user via grant to the user, one of their groups, or via a collection
   */
  SHARED: "SHARED",
  /**
   * Projects shared with the querying user's org or through a collection shared with the querying user's org
   */
  ORG: "ORG",
  /**
   * Projects that the querying user can access via any ownership or sharing method
   */
  ALL: "ALL",
} as const;
export type HexOwnershipLevel = EnumValues<typeof HexOwnershipLevel>;

export const HexFilter = {
  ONLY: "ONLY",
  EXCLUDE: "EXCLUDE",
  INCLUDE: "INCLUDE",
} as const;
export type HexFilter = EnumValues<typeof HexFilter>;

export const ArchiveFilter = {
  ONLY: "ONLY",
  EXCLUDE: "EXCLUDE",
  INCLUDE: "INCLUDE",
} as const;
export type ArchiveFilter = EnumValues<typeof ArchiveFilter>;

export const TrashedFilter = {
  ONLY: "ONLY",
  EXCLUDE: "EXCLUDE",
  INCLUDE: "INCLUDE",
} as const;
export type TrashedFilter = EnumValues<typeof TrashedFilter>;

export const UserActiveFilter = {
  ONLY_ACTIVE: "ONLY_ACTIVE",
  ONLY_INACTIVE: "ONLY_INACTIVE",
  BOTH: "BOTH",
} as const;
export type UserActiveFilter = EnumValues<typeof UserActiveFilter>;

export const PendingReviewFilter = {
  MY_REVIEW: "MY_REVIEW",
  ANY_REVIEW: "ANY_REVIEW",
  UNFILTERED: "UNFILTERED",
} as const;
export type PendingReviewFilter = EnumValues<typeof PendingReviewFilter>;

export const HexOrder = {
  LAST_EDIT: "LAST_EDIT",
  LAST_PUBLISH: "LAST_PUBLISH",
  RECENTLY_VIEWED: "RECENTLY_VIEWED",
  APP_ONLY_RECENTLY_VIEWED: "APP_ONLY_RECENTLY_VIEWED",
  LAST_SEVEN_DAYS_VIEW_COUNT: "LAST_SEVEN_DAYS_VIEW_COUNT",
  LAST_FOURTEEN_DAY_VIEW_COUNT: "LAST_FOURTEEN_DAY_VIEW_COUNT",
  LAST_THIRTY_DAY_VIEW_COUNT: "LAST_THIRTY_DAY_VIEW_COUNT",
  TOTAL_VIEW_COUNT: "TOTAL_VIEW_COUNT",
  CREATED_AT: "CREATED_AT",
  PROJECT_NAME: "PROJECT_NAME",
  OWNER: "OWNER",
  CREATOR: "CREATOR",
  STATUS: "STATUS",
  CATEGORY: "CATEGORY",
  ARCHIVED_AT: "ARCHIVED_AT",
  MY_ACCESS: "MY_ACCESS",
} as const;
export type HexOrder = EnumValues<typeof HexOrder>;

export const AgentChatThreadOwnershipLevel = {
  /**
   * Threads created directly by the querying user
   */
  OWN: "OWN",
  /**
   * Any thread the querying user has access to
   */
  ALL: "ALL",
} as const;
export type AgentChatThreadOwnershipLevel = EnumValues<
  typeof AgentChatThreadOwnershipLevel
>;

export const LastRunStatus = {
  SUCCESS: "SUCCESS",
  /**
   * failed at maxAttempts, no more attempts in progress
   */
  FAILED: "FAILED",
  /**
   * if the current run is in progress, or the previous failed and the run is retrying
   */
  PENDING: "PENDING",
} as const;
export type LastRunStatus = EnumValues<typeof LastRunStatus>;

export const ScheduleEnabled = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
} as const;
export type ScheduleEnabled = EnumValues<typeof ScheduleEnabled>;

export const AOLogOrder = {
  NEW_FIRST: "NEW_FIRST",
  OLD_FIRST: "OLD_FIRST",
} as const;
export type AOLogOrder = EnumValues<typeof AOLogOrder>;

export const HexVersionHistoryEventFilter = {
  VERSION: "VERSION",
  EDIT: "EDIT",
} as const;
export type HexVersionHistoryEventFilter = EnumValues<
  typeof HexVersionHistoryEventFilter
>;

export const HexVersionHistoryVersionFilter = {
  REVIEWED: "REVIEWED",
  PUBLISHED: "PUBLISHED",
} as const;
export type HexVersionHistoryVersionFilter = EnumValues<
  typeof HexVersionHistoryVersionFilter
>;

export const HexColor = {
  RED: "RED",
  ORANGE: "ORANGE",
  YELLOW: "YELLOW",
  LIME: "LIME",
  GREEN: "GREEN",
  TEAL: "TEAL",
  BLUE: "BLUE",
  PURPLE: "PURPLE",
  PINK: "PINK",
  GRAY: "GRAY",
} as const;
export type HexColor = EnumValues<typeof HexColor>;

export const CacheSource = {
  LOGIC_VIEW: "LOGIC_VIEW",
  SCHEDULED_RUN: "SCHEDULED_RUN",
  APP_LOAD: "APP_LOAD",
} as const;
export type CacheSource = EnumValues<typeof CacheSource>;

export const MetricCellComparisonType = {
  VALUE: "VALUE",
  PERCENTAGE: "PERCENTAGE",
  ABSOLUTE: "ABSOLUTE",
} as const;
export type MetricCellComparisonType = EnumValues<
  typeof MetricCellComparisonType
>;

export const MetricCellComparisonFormat = {
  POSITIVE_NEGATIVE: "POSITIVE_NEGATIVE",
  INVERTED_POSITIVE_NEGATIVE: "INVERTED_POSITIVE_NEGATIVE",
} as const;
export type MetricCellComparisonFormat = EnumValues<
  typeof MetricCellComparisonFormat
>;

export const CustomerType = {
  CUSTOMER: "CUSTOMER",
  TEST: "TEST",
  SINGLE_TENANT: "SINGLE_TENANT",
} as const;
export type CustomerType = EnumValues<typeof CustomerType>;

export const SupportedGitProviders = {
  GITHUB: "GITHUB",
  GITLAB: "GITLAB",
  BITBUCKET: "BITBUCKET",
} as const;
export type SupportedGitProviders = EnumValues<typeof SupportedGitProviders>;

export const VcsConnectionType = {
  GITHUB_SYNC: "GITHUB_SYNC",
  GITLAB_SYNC: "GITLAB_SYNC",
  BITBUCKET_SYNC: "BITBUCKET_SYNC",
  GITHUB_SERVER_SYNC: "GITHUB_SERVER_SYNC",
  GITHUB: "GITHUB", // For GitHub package import
  GITHUB_SERVER_PACKAGE_IMPORT: "GITHUB_SERVER_PACKAGE_IMPORT",
  GITLAB_PACKAGE_IMPORT: "GITLAB_PACKAGE_IMPORT",
  GITLAB_SERVER_PACKAGE_IMPORT: "GITLAB_SERVER_PACKAGE_IMPORT",
  BITBUCKET_PACKAGE_IMPORT: "BITBUCKET_PACKAGE_IMPORT",
} as const;
export type VcsConnectionType = EnumValues<typeof VcsConnectionType>;

export const VcsPackageType = {
  CONTEXT: "CONTEXT",
  NOTEBOOK_PACKAGE: "NOTEBOOK_PACKAGE",
} as const;
export type VcsPackageType = EnumValues<typeof VcsPackageType>;

// Used for both Github and Gitlab
export const GithubRefType = {
  BRANCH: "BRANCH",
  TAG: "TAG",
  COMMIT: "COMMIT",
} as const;
export type GithubRefType = EnumValues<typeof GithubRefType>;

export const AppMetadataField = {
  /** @deprecated use "name" */
  TITLE: "TITLE",
  NAME: "NAME",
  DESCRIPTION: "DESCRIPTION",
  AUTHOR: "AUTHOR",
  LAST_EDITED: "LAST_EDITED",
  LAST_RUN: "LAST_RUN",
  CATEGORIES: "CATEGORIES",
  STATUS: "STATUS",
  TABLE_OF_CONTENTS: "TABLE_OF_CONTENTS",
} as const;
export type AppMetadataField = EnumValues<typeof AppMetadataField>;

export const GridElementType = {
  CELL: "CELL",
  SHARED_FILTER: "SHARED_FILTER",
} as const;
export type GridElementType = EnumValues<typeof GridElementType>;

export const ScheduledRunNotificationType = {
  SUCCESS: "SUCCESS",
  FAILURE: "FAILURE",
  ALL: "ALL",
} as const;
export type ScheduledRunNotificationType = EnumValues<
  typeof ScheduledRunNotificationType
>;

export const ScreenshotFormatLiteral = z.enum(["png", "pdf"]);
export type ScreenshotFormat = z.infer<typeof ScreenshotFormatLiteral>;
export const ScreenshotFormat = getNormalEnum(ScreenshotFormatLiteral);

export const ScreenshotTargetLiteral = z.enum(["APP", "TABS", "CELL"]);
export type ScreenshotTarget = z.infer<typeof ScreenshotTargetLiteral>;
export const ScreenshotTarget = getNormalEnum(ScreenshotTargetLiteral);

export const NotificationDestination = {
  SLACK_CHANNEL: "SLACK_CHANNEL",
  /**
   * When this is the notification destination type, we use the Hex user's notification preferences to determine how to deliver the notification.
   */
  USER_PREFERENCE: "USER_PREFERENCE",
} as const;
export type NotificationDestination = EnumValues<
  typeof NotificationDestination
>;

export const NotificationTypeLiteral = z.enum([
  "COMMENT",
  "PROJECT_SHARE",
  "HEX_ROLE_REQUEST",
  "ORG_ROLE_REQUEST",
  "SCHEDULED_RUN_FAILURE",
  "SCHEDULED_RUN_SUCCESS",
  "ACCOUNT_CREATION",
  "TEXT_CELL_MENTION",
  "INITIAL_TOKEN_EXPIRATION",
  "FINAL_TOKEN_EXPIRATION",
  "INITIAL_OAUTH_TOKEN_EXPIRATION",
  "FINAL_OAUTH_TOKEN_EXPIRATION",
  "ADMIN_HEX_ROLE_GRANTED",
  "CONNECTED_APP",
  "DATA_CREDENTIAL_REQUEST",
  "MANUAL_ORG_CREATION",
  "ARCHIVE_DIGEST",
  "PROJECT_LIFECYCLE",
  "REVIEW_REQUESTED",
  "REVIEW_CREATED",
  "REVIEW_REQUEST_RESOLVED",
  "COMPUTE_SPEND_LIMIT",
  "COMPONENT_UPGRADE_AVAILABLE",
  "COMPONENT_IMPORT_CELL_UPDATED",
  "EMBEDDING_RATE_LIMIT_WARNING",
  "EMBEDDING_USAGE_LIMIT_WARNING",
  "ORG_DOMAIN_VERIFICATION_LINK",
  "EMAIL_VERIFICATION_LINK",
  "AGENT_FEEDBACK_FLAGGED",
  "TASK_COMPLETION",
  "TASK_CREDIT_LIMIT",
  "MODEL_DATA_RETENTION_SETTING_CHANGED",
  "AGENT_MESSAGE",
  "EVAL_SCHEDULE_FAILURE",
  "EVAL_SCHEDULE_FAILURE_FOR_ADMINS",
]);

export const NotificationType = getNormalEnum(NotificationTypeLiteral);

export type NotificationType = EnumValues<typeof NotificationType>;

export type InAppNotificationType = Exclude<
  NotificationType,
  | "MANUAL_ORG_CREATION"
  | "ACCOUNT_CREATION"
  | "DATA_CREDENTIAL_REQUEST"
  | "COMPUTE_SPEND_LIMIT"
  | "EMBEDDING_RATE_LIMIT_WARNING"
  | "EMBEDDING_USAGE_LIMIT_WARNING"
  | "ORG_DOMAIN_VERIFICATION_LINK"
  | "EMAIL_VERIFICATION_LINK"
  | "TASK_COMPLETION"
  | "TASK_CREDIT_LIMIT"
  | "MODEL_DATA_RETENTION_SETTING_CHANGED"
  | "AGENT_MESSAGE"
  | "EVAL_SCHEDULE_FAILURE"
  | "EVAL_SCHEDULE_FAILURE_FOR_ADMINS"
>;

export const NotificationStatus = {
  READ: "READ",
  UNREAD: "UNREAD",
} as const;
export type NotificationStatus = EnumValues<typeof NotificationStatus>;

export const NotificationMethod = {
  EMAIL: "EMAIL",
  SLACK: "SLACK",
} as const;
export type NotificationMethod = EnumValues<typeof NotificationMethod>;

export const DataConnectionLinkType = {
  WORKSPACE: "WORKSPACE",
  PROJECT: "PROJECT",
} as const;
export type DataConnectionLinkType = EnumValues<typeof DataConnectionLinkType>;

export const DataConnectionRefreshSection = {
  DATABASES: "DATABASES",
  SCHEMAS: "SCHEMAS",
  TABLES: "TABLES",
  COLUMNS: "COLUMNS",
} as const;
export type DataConnectionRefreshSection = EnumValues<
  typeof DataConnectionRefreshSection
>;

export const OAuthMigrationStatus = {
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  ERRORED: "ERRORED",
} as const;
export type OAuthMigrationStatus = EnumValues<typeof OAuthMigrationStatus>;

export const MessageIntent = {
  SUCCESS: "SUCCESS",
  WARNING: "WARNING",
  ERROR: "ERROR",
  NONE: "NONE",
} as const;
export type MessageIntent = EnumValues<typeof MessageIntent>;

export const ScheduleCadence = {
  HOURLY: "HOURLY",
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
  CUSTOM: "CUSTOM",
} as const;
export type ScheduleCadence = EnumValues<typeof ScheduleCadence>;

export const AllowedScheduleCadenceForExplorers: {
  [K in ScheduleCadence]: boolean;
} = {
  HOURLY: false,
  DAILY: true,
  WEEKLY: true,
  MONTHLY: true,
  CUSTOM: false,
};

export const RunTrigger = {
  SCHEDULE: "SCHEDULE",
  API: "API",
  APP_REFRESH: "APP_REFRESH",
  PUBLISH: "PUBLISH",
  RUN_ALL: "RUN_ALL",
  RUN_CELL: "RUN_CELL",
  INPUT_CHANGE: "INPUT_CHANGE",
  BUTTON: "BUTTON",
  AGENT_RUN_DAG: "AGENT_RUN_DAG",
  APP_VIEW: "APP_VIEW",
} as const;
export type RunTrigger = EnumValues<typeof RunTrigger>;

export const TaoRunStatus = {
  RUNNING: "RUNNING",
  FINISHED: "FINISHED",
  FINISHED_WITH_ERRORS: "FINISHED_WITH_ERRORS",
  ERRORED: "ERRORED",
  DISCONNECTED: "DISCONNECTED",
} as const;
export type TaoRunStatus = EnumValues<typeof TaoRunStatus>;

export const RunTypeFilter = {
  SCHEDULED: "SCHEDULED",
  API: "API",
  APP_REFRESH: "APP_REFRESH",
  ALL: "ALL",
} as const;
export type RunTypeFilter = EnumValues<typeof RunTypeFilter>;

// intentionally lowercase for backcompat
export const ExportType = {
  csv: "csv",
  parquet: "parquet",
  tablestore: "tablestore",
  preview: "preview",
} as const;

export type ExportType = EnumValues<typeof ExportType>;

export const FileUploadTypeLiteral = z.enum([
  "CSV",
  "EXCEL_OPENXML",
  "EXCEL_LEGACY",
]);
export type FileUploadType = z.infer<typeof FileUploadTypeLiteral>;
export const FileUploadType = getNormalEnum(FileUploadTypeLiteral);

export const HexTypeLiteral = z.enum(["PROJECT", "COMPONENT", "EXPLORE"]);
export type HexType = z.infer<typeof HexTypeLiteral>;
export const HexType = getNormalEnum(HexTypeLiteral);

export const StarredTypeLiteral = z.enum([
  ...HexTypeLiteral.options,
  "COLLECTION",
  "THREAD",
]);
export type StarredType = z.infer<typeof StarredTypeLiteral>;
export const StarredType = getNormalEnum(StarredTypeLiteral);

export const SearchHexesField = {
  TITLE: "TITLE",
  DESCRIPTION: "DESCRIPTION",
  SQL_CODE: "SQL_CODE",
  PY_CODE: "PY_CODE",
  R_CODE: "R_CODE",
  MARKDOWN_CODE: "MARKDOWN_CODE",
  TEXT_CELLS: "TEXT_CELLS",
} as const;
export type SearchHexesField = EnumValues<typeof SearchHexesField>;

/** `SearchHexesForHomeField`: This is an extended subset of the `SearchHexesField` which delineates fields by which we support text matching in our project list filters
 * These fields separate from `SearchHexesField` because are consumed by an endpoint that queries our postgres database, as opposed too our elasticsearch index,
 * and for now the two APIs do not support text matching for the same fields.
 */
export const SearchHexesForHomeField = {
  TITLE: SearchHexesField.TITLE,
  CREATOR: "CREATOR",
} as const;
export type SearchHexesForHomeField = EnumValues<
  typeof SearchHexesForHomeField
>;
export const SEARCH_HEXES_FOR_HOME_DEFAULT_FIELDS = [
  SearchHexesForHomeField.TITLE,
  SearchHexesForHomeField.CREATOR,
];
export const SEARCH_HEXES_FOR_HOME_FIELD_LABELS = {
  [SearchHexesForHomeField.TITLE]: "Title",
  [SearchHexesForHomeField.CREATOR]: "Creator",
};

export const CollectionOwnershipLevel = {
  OWN: "OWN",
  SHARED: "SHARED",
  ORG: "ORG",
  ALL: "ALL",
} as const;
export type CollectionOwnershipLevel = EnumValues<
  typeof CollectionOwnershipLevel
>;

export const CollectionOrder = {
  NAME: "NAME",
  RECENT_ADDITION: "RECENT_ADDITION",
} as const;
export type CollectionOrder = EnumValues<typeof CollectionOrder>;

export const KernelSourceImageVersionStateLiteral = z.enum([
  "BUILDING",
  "READY",
  "PRE_BUILD_ERRORED",
  "ERRORED",
]);
export const KernelSourceImageVersionState = getNormalEnum(
  KernelSourceImageVersionStateLiteral,
);
export type KernelSourceImageVersionState = EnumValues<
  typeof KernelSourceImageVersionState
>;

export const KernelSourceImagePrewarmedLiteral = z.enum(["FAST_LAUNCH"]);
export const KernelSourceImagePrewarmed = getNormalEnum(
  KernelSourceImagePrewarmedLiteral,
);
export type KernelSourceImagePrewarmed = EnumValues<
  typeof KernelSourceImagePrewarmed
>;

export const KernelSourceImageStateLiteral = z.enum([
  "REBUILDING",
  "PENDING",
  "BUILDING",
  "READY",
  "PRE_BUILD_ERRORED",
  "ERRORED",
]);
export const KernelSourceImageState = getNormalEnum(
  KernelSourceImageStateLiteral,
);
export type KernelSourceImageState = EnumValues<typeof KernelSourceImageState>;

export const KernelSourceTypeLiteral = z.enum(["V0", "V1"]);

export const KernelSourceImageType = getNormalEnum(KernelSourceTypeLiteral);
export type KernelSourceImageType = EnumValues<typeof KernelSourceImageType>;

export const KernelSourceImageVersionType = getNormalEnum(
  KernelSourceTypeLiteral,
);
export type KernelSourceImageVersionType = EnumValues<
  typeof KernelSourceImageVersionType
>;

export const S3ResourceType = {
  AGENT_THREAD_ARTIFACTS: "AGENT_THREAD_ARTIFACTS",
  DATA_APP: "DATA_APP",
  EXPORTS: "EXPORTS",
  FILES: "FILES",
  SCREENSHOTS: "SCREENSHOTS",
} as const;
export type S3ResourceType = EnumValues<typeof S3ResourceType>;

/**
 * Type of agent making a request.
 *
 * @see AGENT_TYPE_HEADER
 */
export const AgentType = {
  APP_SERVER: "APP_SERVER",
  DATA_SERVICE: "DATA_SERVICE",
  KERNEL: "KERNEL",
  SIDECAR: "SIDECAR",
  WEB_CLIENT: "WEB_CLIENT",
  MULTIPLAYER_SERVICE: "MULTIPLAYER_SERVICE",
} as const;
export type AgentType = EnumValues<typeof AgentType>;

export const ClientTypeEnumLiterals = z.enum([
  "USER",
  "API",
  "SERVER",
  "AI_AGENT",
]);

export const ClientType = getNormalEnum(ClientTypeEnumLiterals);
export type ClientType = z.infer<typeof ClientTypeEnumLiterals>;

export const ApiClientTypeEnumLiterals = z.enum([
  /** Directly dereferences to a USER client, used for personal access tokens */
  "USER",
  /** Used only for access via StrongDM */
  "SDM_USER",
  /** Used by the kernel pod to authenticate as an app session. */
  "APP_SESSION",
  /** Used for authenticating notion preview links */
  "NOTION_PREVIEW",
  /** Legacy - should use SCREENSHOT_SERVICE_USER or SCREENSHOT_SERVICE_BACKGROUND */
  "SCREENSHOT_SERVICE",
  /** Used for user-initiated screenshots that can be directly attributed to and authed for a user */
  "SCREENSHOT_SERVICE_USER",
  /** Used for screenshots captured via background processes */
  "SCREENSHOT_SERVICE_BACKGROUND",
  /** Used for agent-initiated screenshots of draft data apps */
  "SCREENSHOT_SERVICE_AGENT",
  "TABLE_PREVIEW",
  "WORKSPACE_TOKEN",
  "EMBEDDED_USER",
  /** Used similiar to NOTION_PREVIEW but specifically for the MCP app extension */
  "MCP_APP",
  /** Identity for a plain-code project edit/build workload pod. */
  "TAO_BUILD_POD",
  /** Identity for a published Tao app workload pod. */
  "TAO_APP_POD",
  /** Directly dereferences to an AI_AGENT client, used for internal use cases when an ai agent needs bearer auth */
  "AI_AGENT",
  /** Used for MP service operations */
  "MULTIPLAYER_SERVICE",
]);

export const ApiClientType = getNormalEnum(ApiClientTypeEnumLiterals);
export type ApiClientType = z.infer<typeof ApiClientTypeEnumLiterals>;

export const PromoTypeLiteral = z.enum(["EXTENDED_TRIAL"]);

export type PromoType = z.infer<typeof PromoTypeLiteral>;
export const PromoType = getNormalEnum(PromoTypeLiteral);

export const PromoCategoryLiteral = z.enum([
  "PARTNERSHIPS",
  "VC",
  "DEMAND_GEN",
  "EDUCATORS",
  "STARTUP_PROGRAMS",
  "OTHER",
]);

export type PromoCategory = z.infer<typeof PromoCategoryLiteral>;
export const PromoCategory = getNormalEnum(PromoCategoryLiteral);

export const OAuthTokenAttribution = {
  CONNECTION: "CONNECTION",
  USER: "USER",
} as const;
export type OAuthTokenAttribution = EnumValues<typeof OAuthTokenAttribution>;

export const GoogleDriveOauthStatus = {
  SUCCESS: "SUCCESS",
  NO_REFRESH_TOKEN: "NOREFRESHTOKEN",
  USER_DECLINED: "USERDECLINED",
  WRONG_EMAIL: "WRONGEMAIL",
  ACCOUNT_NOT_FOUND: "ACCOUNTNOTFOUND",
  MISSING_DRIVE_SCOPE: "MISSINGDRIVESCOPE",
  INVALID_STATE: "INVALIDSTATE",
} as const;
export type GoogleDriveOauthStatus = EnumValues<typeof GoogleDriveOauthStatus>;

export const GoogleDriveEditAccess = {
  CAN_EDIT: "CAN_EDIT",
  READ_ONLY: "READ_ONLY",
  NO_ACCESS: "NO_ACCESS",
} as const;
export type GoogleDriveEditAccess = EnumValues<typeof GoogleDriveEditAccess>;

export const GoogleDriveFileTypeLiteral = z.enum([
  "REGULAR",
  "GOOGLE_SHEET",
  "GOOGLE_DOC",
]);
export type GoogleDriveFileType = z.infer<typeof GoogleDriveFileTypeLiteral>;
export const GoogleDriveFileType = getNormalEnum(GoogleDriveFileTypeLiteral);

/**
 * Shared asset specific capabilties
 */
export const ExternalFileIntegrationCap = {
  /**
   * Can write files to the storage service
   */
  WRITE_BACK: "WRITE_BACK",
} as const;
export type ExternalFileIntegrationCap = EnumValues<
  typeof ExternalFileIntegrationCap
>;

export const CsvDelimiterLiteral = z.enum(["TAB"]);
export type CsvDelimiter = z.infer<typeof CsvDelimiterLiteral>;
export const CsvDelimiter = getNormalEnum(CsvDelimiterLiteral);

export const DataSourceDatabaseSaveStatus = {
  COMPLETE: "COMPLETE",
  INCOMPLETE: "INCOMPLETE",
} as const;

export type DataSourceDatabaseSaveStatus = EnumValues<
  typeof DataSourceDatabaseSaveStatus
>;

export const MagicUsageStatusLiteral = z.enum([
  "INCLUDED", // Magic will include usage of this data source
  "EXCLUDED", // Magic will not include usage of this data source
]);
export type MagicUsageStatus = z.infer<typeof MagicUsageStatusLiteral>;
export const MagicUsageStatus = getNormalEnum(MagicUsageStatusLiteral);

export const DataSourcesLiteral = z.enum([
  "DATA_CONNECTION",
  "DATABASE",
  "SCHEMA",
  "TABLE",
  "COLUMN",
]);

export type DataSources = z.infer<typeof DataSourcesLiteral>;
export const DataSources = getNormalEnum(DataSourcesLiteral);

export const dataSourceToDisplayText = (dataSource: DataSources): string => {
  switch (dataSource) {
    case DataSources.DATA_CONNECTION:
      return "data connection";
    case DataSources.DATABASE:
      return "database";
    case DataSources.SCHEMA:
      return "schema";
    case DataSources.TABLE:
      return "table";
    case DataSources.COLUMN:
      return "column";
    default:
      return assertNever(dataSource, dataSource);
  }
};

const ViewTypeLiteral = z.enum([
  "PUBLISHED_LATEST",
  "PUBLISHED_COLLAB",
  "PUBLISHED_SNAPSHOT",
  "PUBLISHED_NOTION_EMBED",
  "LOGIC_VIEW",
  "PUBLISHED_VIEW_COLLAB",
  "PUBLISHED_VIEW_LATEST",
  "PUBLISHED_VIEW_SIGNED_EMBEDDED",
]);
export type ViewType = z.infer<typeof ViewTypeLiteral>;
export const ViewType = getNormalEnum(ViewTypeLiteral);

export const ViewDataOpenedFromLiteral = z.enum([
  "PUBLISHED_APP_TOOLBAR",
  "FILTER_POPOVER",
]);
export type ViewDataOpenedFrom = z.infer<typeof ViewDataOpenedFromLiteral>;
export const ViewDataOpenedFrom = getNormalEnum(ViewDataOpenedFromLiteral);

export const DockerImageTagPullStatusLiteral = z.enum([
  "IMAGE_PULL_FAILED",
  "IMAGE_PULL_SUCCEEDED",
]);
export type DockerImageTagPullStatus = z.infer<
  typeof DockerImageTagPullStatusLiteral
>;
export const DockerImageTagPullStatus = getNormalEnum(
  DockerImageTagPullStatusLiteral,
);

export const DataConnectionLanceStatusLiteral = z.enum([
  "UP_TO_DATE",
  "STALE",
  "ERRORED",
]);

export type DataConnectionLanceStatus = z.infer<
  typeof DataConnectionLanceStatusLiteral
>;
export const DataConnectionLanceStatus = getNormalEnum(
  DataConnectionLanceStatusLiteral,
);

export const KernelDirectoryLiteral = z.enum(["CELL_UPLOADS"]);
export type KernelDirectory = z.infer<typeof KernelDirectoryLiteral>;
export const KernelDirectory = getNormalEnum(KernelDirectoryLiteral);

export const kernelDirectoryToName = (
  kernelDirectory: KernelDirectory,
): string => {
  switch (kernelDirectory) {
    case KernelDirectory.CELL_UPLOADS:
      return "cell_uploads";
    default:
      assertNever(kernelDirectory, kernelDirectory);
  }
};

export const FileTypeLiteral = z.enum(["APP_SESSION_FILE", "FILE"]);
export type FileType = z.infer<typeof FileTypeLiteral>;
export const FileType = getNormalEnum(FileTypeLiteral);

/**
 * Repesents the present status of a in progress review request
 */
export const ReviewRequestStatus = {
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  DENIED: "DENIED",
} as const;
export type ReviewRequestStatus = EnumValues<typeof ReviewRequestStatus>;

export function humanReadableReviewRequestStatus(
  status: ReviewRequestStatus,
  reset: boolean,
): string {
  if (reset) {
    return "re-review pending";
  }
  return {
    [ReviewRequestStatus.PENDING]: "review pending",
    [ReviewRequestStatus.APPROVED]: "approved",
    [ReviewRequestStatus.DENIED]: "changes requested",
  }[status];
}

/**
 * Why a reviewer's approval is not a valid approval of a specific hex version
 * for publishing. Produced by `evaluateReviewRequestLinkApproval` in
 * `reviewsUtils`, which also owns the human-readable copy.
 */
export const InvalidReviewApprovalReason = {
  /** Link targets a group; the approver must convert it to an individual link */
  GROUP_TARGET: "GROUP_TARGET",
  /** A re-review was requested after this reviewer's last determination */
  RESET: "RESET",
  /** The reviewer requested changes */
  CHANGES_REQUESTED: "CHANGES_REQUESTED",
  /** The reviewer has not made a determination */
  NOT_APPROVED: "NOT_APPROVED",
  /** The reviewer's workspace role is below the minimum that may approve */
  INVALID_WORKSPACE_ROLE: "INVALID_WORKSPACE_ROLE",
  /** The approval was for a different version than the one being published */
  STALE_VERSION: "STALE_VERSION",
  /** The link is approved but no approving review exists for this reviewer */
  NO_MATCHING_REVIEW: "NO_MATCHING_REVIEW",
} as const;
export type InvalidReviewApprovalReason = EnumValues<
  typeof InvalidReviewApprovalReason
>;

/**
 * Repesents the status of a review request, updated once it is either closed
 * or published
 */
export const ReviewRequestResult = {
  PENDING: "PENDING",
  ACCEPTED: "ACCEPTED",
  CANCELLED: "CANCELLED",
} as const;
export type ReviewRequestResult = EnumValues<typeof ReviewRequestResult>;

export function humanReadableReviewRequestResult(
  result: ReviewRequestResult,
): string {
  return {
    [ReviewRequestResult.PENDING]: "Review pending",
    [ReviewRequestResult.ACCEPTED]: "Published",
    [ReviewRequestResult.CANCELLED]: "Closed",
  }[result];
}

/**
 * Result of an individual review on a review request
 */
export const ReviewDeterminiation = {
  /**
   * Did not make a determination, just left a comment
   */
  COMMENTED: "COMMENTED",
  APPROVED: "APPROVED",
  DENIED: "DENIED",
} as const;
export type ReviewDeterminiation = EnumValues<typeof ReviewDeterminiation>;

export const DataBrowserTabLiteral = z.enum([
  "DATA",
  "RECENTLY_USED",
  "FAVORITES",
  "SEMANTIC_LAYER",
  "CURATION",
  "RECOMMENDED",
]);
export const DataBrowserTab = getNormalEnum(DataBrowserTabLiteral);
export type DataBrowserTab = z.infer<typeof DataBrowserTabLiteral>;

/**
 * Indicates the type of reviews being used for a project. Review systems are mutually
 * exclusive.
 */
export const ReviewSystem = {
  NONE: "NONE",
  LEGACY_GIT_SYNC: "LEGACY_GIT_SYNC",
  HEX: "HEX",
} as const;
export type ReviewSystem = EnumValues<typeof ReviewSystem>;

export const VcsPackageAuthorizationTypeLiteral = z.enum([
  "NONE",
  "BEARER",
  "BASIC",
]);
export type VcsPackageAuthorizationType = z.infer<
  typeof VcsPackageAuthorizationTypeLiteral
>;
export const VcsPackageAuthorizationType = getNormalEnum(
  VcsPackageAuthorizationTypeLiteral,
);

export const ChartDisplayTypeLiteral = z.enum(["CHART", "TABLE"]);
export const ChartDisplayType = getNormalEnum(ChartDisplayTypeLiteral);
export type ChartDisplayType = z.infer<typeof ChartDisplayTypeLiteral>;

export const IconTypeLiteral = z.enum([
  "chexagon",
  "certified",
  "trophy",
  "heart",
  "medal",
  "computerchip",
  "thumbsup",
  "blessed",
  "experiment",
  "lightbulb",
  "megaphone",
  "fire",
  "pluscircle",
  "checksquare",
  "notificationunread",
  "bug",
  "gem",
  "private",
  "unlock",
  "success",
  "reviewinprogress",
  "addreaction",
  "quickstart",
  "envelope",
  "key",
  "sparkles",
  "lightning",
  "star",
  "clock",
  "folder",
]);
export const IconType = getNormalEnum(IconTypeLiteral);
export type IconType = z.infer<typeof IconTypeLiteral>;

export const MagicTypeAheadProviderLiteral = z.enum(["HEX_TYPEAHEAD", "OFF"]);
export type MagicTypeAheadProvider = z.infer<
  typeof MagicTypeAheadProviderLiteral
>;
export const MagicTypeAheadProvider = getNormalEnum(
  MagicTypeAheadProviderLiteral,
);

/**
 * These are the types of ways that kernel pods can be allocated.
 */
export const KernelAllocationKindTypeLiterals = z.enum([
  /**
   * These are kernel pods that were prewarmed and then claimed by a user.
   */
  "CLAIMED_PREWARMED_KERNEL_POD",
  /**
   * These are kernel pods that were created on demand when there are no prewarmed kernel pods available.
   */
  "ON_DEMAND_KERNEL_POD",
]);
export const KernelAllocationKindType = getNormalEnum(
  KernelAllocationKindTypeLiterals,
);
export type KernelAllocationKindType = z.infer<
  typeof KernelAllocationKindTypeLiterals
>;

/**
 * These are the variants of AppSessions; see sessionTypeForAttribution() for more details.
 */
export const AppSessionAttributionTypeLiterals = z.enum([
  "UNKNOWN",
  "LOGIC",
  "SCHEDULED",
  "API",
  "APP_REFRESH",
  "PUBLISH_PREVIEW",
  "PUBLISHED_APP",
  "PRESIGNED_EMBEDDED_APP",
  "EXPLORE",
  "THREAD",
  "CUSTOMER_EVAL_THREAD",
]);
export const AppSessionAttributionType = getNormalEnum(
  AppSessionAttributionTypeLiterals,
);
export type AppSessionAttributionType = z.infer<
  typeof AppSessionAttributionTypeLiterals
>;

export const AppSessionVisibilityTypeLiterals = z.enum([
  "VIEWER_ONLY",
  "PRIVATE_APP_VIEW",
  "UNSHARED_APP_VIEW",
  "SHARED_APP_VIEW",
]);
export const AppSessionVisibilityType = getNormalEnum(
  AppSessionVisibilityTypeLiterals,
);
export type AppSessionVisibilityType = z.infer<
  typeof AppSessionVisibilityTypeLiterals
>;

export const ScheduleNotificationConditionTypeLiterals = z.enum([
  "METRIC_CELL",
  "TABLE_ROW_COUNT",
]);
export const ScheduleNotificationConditionType = getNormalEnum(
  ScheduleNotificationConditionTypeLiterals,
);
export type ScheduleNotificationConditionType = z.infer<
  typeof ScheduleNotificationConditionTypeLiterals
>;

export const ScheduleNotificationConditionPredicateLiterals = z.enum([
  "GT",
  "GTE",
  "EQ",
  "NEQ",
  "LTE",
  "LT",
  "CONTAINS",
  "NOT_CONTAINS",
]);
export type ScheduleNotificationConditionPredicate = z.infer<
  typeof ScheduleNotificationConditionPredicateLiterals
>;
export const ScheduleNotificationConditionPredicate = getNormalEnum(
  ScheduleNotificationConditionPredicateLiterals,
);

export const RunNotificationTrigger = {
  API: "API",
  SCHEDULE: "SCHEDULE",
  TEST_SCHEDULE: "TEST_SCHEDULE",
  BACKGROUND_DATA_REFRESH: "BACKGROUND_DATA_REFRESH",
} as const;
export type RunNotificationTrigger = EnumValues<typeof RunNotificationTrigger>;

export const RunNotificationRunStatus = {
  FAILED: "FAILED",
  SUCCEEDED: "SUCCEEDED",
  TIMED_OUT: "TIMED_OUT",
} as const;
export type RunNotificationRunStatus = EnumValues<
  typeof RunNotificationRunStatus
>;

export const SavedViewParamTypeLiteral = z.enum([
  "INPUT",
  "DISPLAY_TABLE",
  "FILTER_CELL",
  "SHARED_FILTER",
]);
export type SavedViewParamType = z.infer<typeof SavedViewParamTypeLiteral>;
export const SavedViewParamType = getNormalEnum(SavedViewParamTypeLiteral);

export enum EntityDiffType {
  COLUMN = "COLUMN",
  TABLE = "TABLE",
  SCHEMA = "SCHEMA",
  DATABASE = "DATABASE",
}

export enum EntityGrantDiffType {
  DATABASE_GRANT = "DATABASE_GRANT",
  SCHEMA_GRANT = "SCHEMA_GRANT",
  TABLE_GRANT = "TABLE_GRANT",
}

export const CollapsibleCellLabelStyle = {
  AUTO: "AUTO",
  H1: "H1",
  H2: "H2",
  H3: "H3",
  P: "P",
} as const;
export type CollapsibleCellLabelStyle = EnumValues<
  typeof CollapsibleCellLabelStyle
>;

export const KernelExecutorLiteral = z.enum(["JUPYTER", "MINERVA"]);
export type KernelExecutor = z.infer<typeof KernelExecutorLiteral>;
export const KernelExecutor = getNormalEnum(KernelExecutorLiteral);

export const SemanticProjectVersionState = {
  /** Latest, editable version of a semantic project. Not used by downstream consumers. */
  DRAFT: "DRAFT",
  /** Immutable version of a semantic project that might be published after review. */
  PUBLISH_PREVIEW: "PUBLISH_PREVIEW",
  /**
   * Immutable version of a semantic project that has been published.
   * Not necessarily the latest published version.
   */
  PUBLISHED: "PUBLISHED",
  /**
   * Immutable version of a semantic project as a checkpoint before the Agent potentially edits it.
   */
  AGENT_CHECKPOINT: "AGENT_CHECKPOINT",
} as const;
export type SemanticProjectVersionState = EnumValues<
  typeof SemanticProjectVersionState
>;

export const OrgGuideFileVersionState = {
  /** Immutable version of an org guide file that might be applied to the draft version after review. */
  DRAFT_PREVIEW: "DRAFT_PREVIEW",
  /** Latest, editable version of an org guide file. Not used by downstream consumers. */
  DRAFT: "DRAFT",
  /** Immutable version of an org guide file that might be published after review. */
  PUBLISH_PREVIEW: "PUBLISH_PREVIEW",
  /**
   * Immutable version of an org guide file that has been published.
   * Not necessarily the latest published version.
   */
  PUBLISHED: "PUBLISHED",
} as const;
export type OrgGuideFileVersionState = EnumValues<
  typeof OrgGuideFileVersionState
>;

/** The defined branches of an org guide file. */
export const OrgGuideFileBase = {
  /** The single, editable version of an org guide file. */
  DRAFT: "DRAFT",
  /** The last published version of an org guide file. */
  LATEST: "LATEST",
} as const;
export type OrgGuideFileBase = EnumValues<typeof OrgGuideFileBase>;

export const ThreadExtractionEvalType = {
  TASK_COMPLETION: "TASK_COMPLETION",
  CONTEXT_BASED_RESPONSE: "CONTEXT_BASED_RESPONSE",
  AGENT_RETRIEVAL_SUFFICIENCY: "AGENT_RETRIEVAL_SUFFICIENCY",
  USER_CONTEXT_SUFFICIENCY: "USER_CONTEXT_SUFFICIENCY",
  DATA_VISUALIZATION_RELEVANCE: "DATA_VISUALIZATION_RELEVANCE",
  SUB_AGENT_DELEGATION_RATE: "SUB_AGENT_DELEGATION_RATE",
  TABLE_RETRIEVAL_ACCURACY: "TABLE_RETRIEVAL_ACCURACY",
  TOOL_EFFICIENCY: "TOOL_EFFICIENCY",
  TOOL_EXECUTION: "TOOL_EXECUTION",
  TOOL_INPUTS: "TOOL_INPUTS",
  WORKSPACE_GUIDE_ADHERENCE: "WORKSPACE_GUIDE_ADHERENCE",
} as const;
export type ThreadExtractionEvalType = EnumValues<
  typeof ThreadExtractionEvalType
>;

export const ThreadExtractionType = {
  ...ThreadExtractionEvalType,
  SUMMARIZATION: "SUMMARIZATION",
  SUMMARIZATION_EXPERIMENT_V1: "SUMMARIZATION_EXPERIMENT_V1",
  CONFUSION_DETECTION: "CONFUSION_DETECTION",
  CONFUSION_DETECTION_EXPERIMENT_V1: "CONFUSION_DETECTION_EXPERIMENT_V1",
  RETRIEVAL_FAILURE_DETECTION: "RETRIEVAL_FAILURE_DETECTION",
  USER_CONTEXT_EXHAUST_DETECTION: "USER_CONTEXT_EXHAUST_DETECTION",
  ARTIFACT_EXTRACTION: "ARTIFACT_EXTRACTION",
} as const;
export type ThreadExtractionType = EnumValues<typeof ThreadExtractionType>;

export const ResourceAction = {
  USED: "used",
  VIEWED: "viewed",
} as const;
export type ResourceAction = EnumValues<typeof ResourceAction>;

export const ResourceReferenceType = {
  TABLE: "table",
  SEMANTIC_MODEL: "semantic_model",
  SEMANTIC_VIEW: "semantic_view",
  WAREHOUSE_SEMANTIC_VIEW: "warehouse_semantic_view",
  GUIDE: "guide",
  REFERENCE_REPO: "reference_repo",
  PROJECT: "project",
  CODE_PROJECT: "code_project",
  THREAD: "thread",
  WEB: "web",
  HEX_DOCS: "hex_docs",
  MCP_TOOL: "mcp_tool",
  OTHER: "other",
} as const;
export type ResourceReferenceType = EnumValues<typeof ResourceReferenceType>;

export const RESOURCE_REFERENCE_TYPE_DISPLAY_NAME: Record<
  ResourceReferenceType,
  string
> = {
  [ResourceReferenceType.TABLE]: "Table",
  [ResourceReferenceType.SEMANTIC_MODEL]: "Semantic model",
  [ResourceReferenceType.SEMANTIC_VIEW]: "Semantic view",
  [ResourceReferenceType.WAREHOUSE_SEMANTIC_VIEW]: "Warehouse semantic view",
  [ResourceReferenceType.GUIDE]: "Guide",
  [ResourceReferenceType.REFERENCE_REPO]: "Reference repository",
  [ResourceReferenceType.PROJECT]: "Project",
  [ResourceReferenceType.CODE_PROJECT]: "Code project",
  [ResourceReferenceType.THREAD]: "Thread",
  [ResourceReferenceType.WEB]: "Web",
  [ResourceReferenceType.HEX_DOCS]: "Hex docs",
  [ResourceReferenceType.MCP_TOOL]: "MCP tool",
  [ResourceReferenceType.OTHER]: "Other",
};

export function getResourceReferenceTypeDisplayName(
  resourceType: string,
): string {
  return (
    Object.entries(RESOURCE_REFERENCE_TYPE_DISPLAY_NAME).find(
      ([type]) => type === resourceType,
    )?.[1] ?? "Unknown resource type"
  );
}

export const ContextSuggestionSourceType = {
  THREAD_WARNING: "THREAD_WARNING",
  USER_REPORT: "USER_REPORT",
  USER_CONTEXT_EXHAUST: "USER_CONTEXT_EXHAUST",
  // Other sources to be added later
} as const;
export type ContextSuggestionSourceType = EnumValues<
  typeof ContextSuggestionSourceType
>;

export const ContextSuggestionSourceStatus = {
  NEW: "NEW",
  PROCESSED: "PROCESSED",
  FAILED: "FAILED",
} as const;
export type ContextSuggestionSourceStatus = EnumValues<
  typeof ContextSuggestionSourceStatus
>;

export const ContextSuggestionIssueStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED",
  REJECTED: "REJECTED",
} as const;
export type ContextSuggestionIssueStatus = EnumValues<
  typeof ContextSuggestionIssueStatus
>;

export const ContextSuggestionRunStatus = {
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  NO_OP: "NO_OP",
  FAILED: "FAILED",
} as const;
export type ContextSuggestionRunStatus = EnumValues<
  typeof ContextSuggestionRunStatus
>;

export const KnowledgeAtomClass = {
  RESOURCE_SELECTION: "resource_selection",
  DEFINITION: "definition",
  DATA_INTERPRETATION: "data_interpretation",
} as const;
export type KnowledgeAtomClass = EnumValues<typeof KnowledgeAtomClass>;

export const KnowledgeAtomStatus = {
  UNVERIFIED: "unverified",
  BACKED: "backed",
  VERIFIED: "verified",
  CONTESTED: "contested",
  DEBUNKED: "debunked",
} as const;
export type KnowledgeAtomStatus = EnumValues<typeof KnowledgeAtomStatus>;

export const KnowledgeAtomPrimaryBasis = {
  AUTHORITATIVE_RESOURCE: "authoritative_resource",
  NON_AUTHORITATIVE_RESOURCE: "non_authoritative_resource",
  USER_VERIFIED: "user_verified",
  UNCORROBORATED_THREAD_EVIDENCE: "uncorroborated_thread_evidence",
  REPEATED_THREAD_EVIDENCE: "repeated_thread_evidence",
  QUERY_EVIDENCE: "query_evidence",
  MIXED: "mixed",
} as const;
export type KnowledgeAtomPrimaryBasis = EnumValues<
  typeof KnowledgeAtomPrimaryBasis
>;

export const KnowledgeAtomResourceLinkRole = {
  SOURCE_OF_TRUTH: "source_of_truth",
  SUPPORTING: "supporting",
  APPLIES_TO: "applies_to",
  CONFLICTING: "conflicting",
} as const;
export type KnowledgeAtomResourceLinkRole = EnumValues<
  typeof KnowledgeAtomResourceLinkRole
>;

export const AgentAssumptionKnowledgeLinkRole = {
  BASED_ON_KNOWLEDGE: "based_on_knowledge",
  DEBUNKS_KNOWLEDGE: "debunks_knowledge",
} as const;
export type AgentAssumptionKnowledgeLinkRole = EnumValues<
  typeof AgentAssumptionKnowledgeLinkRole
>;

export const AgentAssumptionResolutionType = {
  ATTACHED_TO_KNOWLEDGE: "attached_to_knowledge",
  REJECTED: "rejected",
  DEBUNKED: "debunked",
} as const;
export type AgentAssumptionResolutionType = EnumValues<
  typeof AgentAssumptionResolutionType
>;

export const KnowledgeCurationRunStatus = {
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  PARTIAL: "PARTIAL",
  FAILED: "FAILED",
} as const;
export type KnowledgeCurationRunStatus = EnumValues<
  typeof KnowledgeCurationRunStatus
>;

export const KnowledgeCurationRunPhase = {
  BOOTSTRAP: "BOOTSTRAP",
  INCREMENTAL: "INCREMENTAL",
} as const;
export type KnowledgeCurationRunPhase = EnumValues<
  typeof KnowledgeCurationRunPhase
>;

export const KnowledgeCurationResourceOutcome = {
  ATOMIZED: "atomized",
  NO_REUSABLE_KNOWLEDGE: "no_reusable_knowledge",
  READ_FAILURE: "read_failure",
} as const;
export type KnowledgeCurationResourceOutcome = EnumValues<
  typeof KnowledgeCurationResourceOutcome
>;

export const ContextSuggestionFileUpdateType = {
  CREATE_ORG_GUIDE_FILE: "CREATE_ORG_GUIDE_FILE",
  CREATE_SEMANTIC_PROJECT_FILE: "CREATE_SEMANTIC_PROJECT_FILE",
  EDIT_ORG_GUIDE_FILE: "EDIT_ORG_GUIDE_FILE",
  EDIT_SEMANTIC_PROJECT_FILE: "EDIT_SEMANTIC_PROJECT_FILE",
  EDIT_WORKSPACE_RULES: "EDIT_WORKSPACE_RULES",
  RENAME_SEMANTIC_PROJECT_FILE: "RENAME_SEMANTIC_PROJECT_FILE",
  SEMANTIC_WORKBENCH_TASK: "SEMANTIC_WORKBENCH_TASK",
} as const;
export type ContextSuggestionFileUpdateType = EnumValues<
  typeof ContextSuggestionFileUpdateType
>;

export const ContextSuggestionDataWarehouseUpdateType = {
  UPDATE_DESCRIPTION: "UPDATE_DESCRIPTION",
  UPDATE_STATUS: "UPDATE_STATUS",
} as const;
export type ContextSuggestionDataWarehouseUpdateType = EnumValues<
  typeof ContextSuggestionDataWarehouseUpdateType
>;

export const ContextSuggestionUpdateStatus = {
  NEEDS_REVIEW: "NEEDS_REVIEW",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  STALE: "STALE",
  RESOLVED_EXTERNALLY: "RESOLVED_EXTERNALLY",
} as const;
export type ContextSuggestionUpdateStatus = EnumValues<
  typeof ContextSuggestionUpdateStatus
>;

/**
 * Whether an update can be applied to the workspace.
 *
 * Distinct from `status` on the entity, this reflects live mergeability
 * against moving targets, not human decision.
 */
export const ContextSuggestionFileUpdateApplyReadiness = {
  /** Able to apply the update. */
  READY: "READY",
  /** The external source is able to apply the update. */
  WAITING_ON_EXTERNAL_SOURCE: "WAITING_ON_EXTERNAL_SOURCE",
  /** Unable to apply the update because the status is not in the NEEDS_REVIEW phase. */
  BLOCKED_ON_STATUS: "BLOCKED_ON_STATUS",
  /** Unable to apply the update because it is associated with an outdated run. */
  BLOCKED_ON_OUTDATED: "BLOCKED_ON_OUTDATED",
  /** Unable to apply the update to the current draft base. */
  BLOCKED_ON_DRAFT: "BLOCKED_ON_DRAFT",
  /** Unable to apply the update to the latest (e.g. last-published) base. */
  BLOCKED_ON_LATEST: "BLOCKED_ON_LATEST",
  /** Unable to apply the update to the draft and latest bases. */
  BLOCKED_ON_DRAFT_AND_LATEST: "BLOCKED_ON_DRAFT_AND_LATEST",
} as const;
export type ContextSuggestionFileUpdateApplyReadiness = EnumValues<
  typeof ContextSuggestionFileUpdateApplyReadiness
>;

/**
 * A version branch of a file that an update can be applied to.
 */
export const ContextSuggestionFilePreviewBase = {
  /**
   * The file version that was used to generate the update. This is an
   * immutable resource which the update should always apply cleanly to.
   */
  REVIEW: "REVIEW",
  /**
   * The current draft file version in the workspace. This is a mutable
   * resource which may drift.
   */
  DRAFT: "DRAFT",
  /**
   * The current latest (e.g. last-published) file version in the workspace.
   * This is a mutable pointer which may have drifted from the review base.
   */
  LATEST: "LATEST",
} as const;
export type ContextSuggestionFilePreviewBase = EnumValues<
  typeof ContextSuggestionFilePreviewBase
>;

/**
 * Why an update could not be applied to a given base.
 */
export const ContextSuggestionFilePreviewErrorCode = {
  /** The updates requires the base file version but it does not exist or has been deleted. */
  BASE_MISSING: "BASE_MISSING",
  /** The update assumes that the base file version does not exist at the suggested file path, but it does. */
  BASE_ALREADY_EXISTS: "BASE_ALREADY_EXISTS",
  /** The contents diff could not be applied to the base (overlapping or incompatible edits). */
  MERGE_CONFLICT: "MERGE_CONFLICT",
  /** The preview operation is not implemented for the file update type. */
  NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
} as const;
export type ContextSuggestionFilePreviewErrorCode = EnumValues<
  typeof ContextSuggestionFilePreviewErrorCode
>;

export const SemanticProjectVersionBuildStatus = {
  RUNNING: "RUNNING",
  /** Build failed unexpectedly during the process */
  FATAL_ERROR: "FATAL_ERROR",
  /** Build completed as expected, but had errors in the result */
  FAILURE: "FAILURE",
  /** Build completed as expected with no errors*/
  SUCCESS: "SUCCESS",
} as const;
export type SemanticProjectVersionBuildStatus = EnumValues<
  typeof SemanticProjectVersionBuildStatus
>;

export const BullQueueNameLiteral = z.enum([
  "SCHEMA_REFRESH_GET_SCHEMA_ERROR_CATCHER_QUEUE",
  "SCHEMA_REFRESH_COMPLETION_QUEUE",
  "SCHEMA_REFRESH_UPDATE_LANCE_QUEUE",
]);
export type BullQueueName = z.infer<typeof BullQueueNameLiteral>;
export const BullQueueName = getNormalEnum(BullQueueNameLiteral);

export const SemanticProjectHistoryOrder = {
  NEW_FIRST: "NEW_FIRST",
  OLD_FIRST: "OLD_FIRST",
} as const;
export type SemanticProjectHistoryOrder = EnumValues<
  typeof SemanticProjectHistoryOrder
>;

export const ContextWorkbenchVersionGroupState = {
  PUBLISHED: "PUBLISHED",
} as const;
export type ContextWorkbenchVersionGroupState = EnumValues<
  typeof ContextWorkbenchVersionGroupState
>;

export const TaskCadence = {
  DAILY: "DAILY",
  WEEKDAYS: "WEEKDAYS",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
} as const;
export type TaskCadence = EnumValues<typeof TaskCadence>;

export const TaskStartHour = {
  H6: 6,
  H8: 8,
  H10: 10,
  H12: 12,
  H14: 14,
  H16: 16,
  H18: 18,
  H20: 20,
  H22: 22,
} as const;
export type TaskStartHour = EnumValues<typeof TaskStartHour>;

export const CustomerEvalSuiteScheduleCadence = {
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
} as const;
export type CustomerEvalSuiteScheduleCadence = EnumValues<
  typeof CustomerEvalSuiteScheduleCadence
>;

export const CustomerEvalScheduleNotificationType = {
  INACTIVE_USER: "INACTIVE_USER",
  INSUFFICIENT_PERMISSIONS: "INSUFFICIENT_PERMISSIONS",
  EVAL_DEFINITION_VALIDATION_ERROR: "EVAL_DEFINITION_VALIDATION_ERROR",
  INSUFFICIENT_CREDITS: "INSUFFICIENT_CREDITS",
} as const;
export type CustomerEvalScheduleNotificationType = EnumValues<
  typeof CustomerEvalScheduleNotificationType
>;

export type CustomerEvalScheduleUserNotificationType =
  | "INSUFFICIENT_PERMISSIONS"
  | "EVAL_DEFINITION_VALIDATION_ERROR"
  | "INSUFFICIENT_CREDITS";

export type CustomerEvalScheduleAdminNotificationType = Exclude<
  CustomerEvalScheduleNotificationType,
  CustomerEvalScheduleUserNotificationType
>;

export const CustomerEvalSuiteScheduleStartHour = {
  H6: 6,
  H8: 8,
  H10: 10,
  H12: 12,
  H14: 14,
  H16: 16,
  H18: 18,
  H20: 20,
  H22: 22,
} as const;
export type CustomerEvalSuiteScheduleStartHour = EnumValues<
  typeof CustomerEvalSuiteScheduleStartHour
>;

/** Agent-facing delivery channel for SendNotification; CreateTask uses {@link TaskDeliveryChannel} instead. */
export const AgentDeliveryChannel = {
  EMAIL: "EMAIL",
  SLACK: "SLACK",
} as const;
export type AgentDeliveryChannel = EnumValues<typeof AgentDeliveryChannel>;

/** Agent-facing delivery channel for CreateTask; NONE saves the task with no automatic notification. */
export const TaskDeliveryChannel = {
  EMAIL: "EMAIL",
  SLACK: "SLACK",
  NONE: "NONE",
} as const;
export type TaskDeliveryChannel = EnumValues<typeof TaskDeliveryChannel>;

export const ContextChangesetBase = {
  DRAFT: "DRAFT",
  LATEST: "LATEST",
} as const;
export type ContextChangesetBase = EnumValues<typeof ContextChangesetBase>;

export const ContextChangesetState = {
  PROPOSED: "PROPOSED",
  APPLIED: "APPLIED",
} as const;
export type ContextChangesetState = EnumValues<typeof ContextChangesetState>;

export const SemanticProjectChangeStatus = {
  MODIFIED: "MODIFIED",
  UNMODIFIED: "UNMODIFIED",
} as const;
export type SemanticProjectChangeStatus = EnumValues<
  typeof SemanticProjectChangeStatus
>;

export const CustomerEvalSuiteChangeStatus = {
  CREATED: "CREATED",
  MODIFIED: "MODIFIED",
  UNMODIFIED: "UNMODIFIED",
} as const;
export type CustomerEvalSuiteChangeStatus = EnumValues<
  typeof CustomerEvalSuiteChangeStatus
>;

export const SemanticResourceChangeStatus = {
  ADDED: "ADDED",
  DELETED: "DELETED",
  MODIFIED: "MODIFIED",
  UNMODIFIED: "UNMODIFIED",
} as const;
export type SemanticResourceChangeStatus = EnumValues<
  typeof SemanticResourceChangeStatus
>;

export const ContextVersionState = {
  /** The current set of draft context resource versions. */
  DRAFT: "DRAFT",
  /** A set of context resource versions that have been published. */
  PUBLISHED: "PUBLISHED",
} as const;
export type ContextVersionState = EnumValues<typeof ContextVersionState>;

export const ReferenceRepoDownloadStateLiterals = z.enum([
  "DOWNLOADING",
  "DOWNLOADED",
  "DOWNLOAD_REFUSED",
  "ZIP_TIMEOUT_ERROR",
  "DOWNLOAD_TIMEOUT_ERROR",
  "GENERIC_ERROR",
]);
export const ReferenceRepoDownloadState = getNormalEnum(
  ReferenceRepoDownloadStateLiterals,
);
export type ReferenceRepoDownloadState = z.infer<
  typeof ReferenceRepoDownloadStateLiterals
>;

export const McpServerConfigurationTypeLiterals = z.enum([
  "PRE_DEFINED",
  "CUSTOM",
]);
export const McpServerConfigurationType = getNormalEnum(
  McpServerConfigurationTypeLiterals,
);
export type McpServerConfigurationType = EnumValues<
  typeof McpServerConfigurationType
>;

export const McpServerConnectionStatusLiterals = z.enum([
  "connected",
  "disabled",
  "not-connected",
]);
export const McpServerConnectionStatus = getNormalEnum(
  McpServerConnectionStatusLiterals,
);
export type McpServerConnectionStatus = EnumValues<
  typeof McpServerConnectionStatus
>;

export const UserMcpServerConnectionAuthType = {
  API_KEY: "API_KEY",
  BEARER: "BEARER",
  BASIC: "BASIC",
  OAUTH: "OAUTH",
  INHERITED: "INHERITED",
} as const;
export type UserMcpServerConnectionAuthType = EnumValues<
  typeof UserMcpServerConnectionAuthType
>;

export const McpServerConfigurationAuthType = {
  /**
   * The org level configuration holds the credentials and is shared across users
   */
  EMBEDDED_HEADER_AUTH: "EMBEDDED_HEADER_AUTH",
  /**
   * OAuth includes both DCR and static clients
   */
  OAUTH: "OAUTH",
  /**
   * Each user must include their own credentials (personal access tokens, etc)
   */
  HEADER_AUTH: "HEADER_AUTH",
} as const;
export type McpServerConfigurationAuthType = EnumValues<
  typeof McpServerConfigurationAuthType
>;
export const KernelManagementView = {
  USER: "USER",
  ORG: "ORG",
} as const;
export type KernelManagementView = EnumValues<typeof KernelManagementView>;

/**
 * Why an org's resolved AI data usage is opted out
 */
export const AiDataUsageOptOutReason = {
  TRIAL: "TRIAL",
  MANUAL_OPT_OUT: "MANUAL_OPT_OUT",
  AI_DISABLED: "AI_DISABLED",
  WORKSPACE_AUTO_OPT_OUT: "WORKSPACE_AUTO_OPT_OUT",
  WORKSPACE_INACTIVE: "WORKSPACE_INACTIVE",
} as const;
export type AiDataUsageOptOutReason = EnumValues<
  typeof AiDataUsageOptOutReason
>;
